<template>
    <!--h4 class="text-success text-center">Api integrated</h4-->

    <div class="p-2 mx-auto d-flex flex-column gap-6" style="width: 95vw;">
        <!-- Page Heading -->
        <div class="dashboard-heading mb-4">
            <div class="dh-icon-wrap dh-icon-org">
                <i class="material-icons">business</i>
            </div>
            <div>
                <h5 class="dh-title">Edit Organization</h5>
                <p class="dh-subtitle">{{ organization.name || 'Loading...' }}</p>
            </div>
        </div>
        <div class="d-flex justify-content-end">
            <ExplainPageButton page-key="organization" />
        </div>
        <!-- Page Loader -->
        <CardLoader v-if="!organization.id" class="my-4" />

        <!-- Organization Form -->
        <FormContainer heading="Edit Organization" v-else>
            <template v-slot:content>
                <form @submit.prevent="saveOrganization">
                    <div class="row">
                        <div class="col-md-6">
                            <div class="mb-3">
                                <material-input id="id" type="text" label="ID" name="id" v-model="organization.id"
                                    :read-only="true" />
                            </div>
                        </div>
                        <div class="col-md-6">
                            <div class="mb-3">
                                <material-input id="name" type="text" label="Name" name="name"
                                    v-model="organization.name" />
                            </div>
                        </div>
                    </div>

                    <div class="row">
                        <div class="col-12">
                            <div class="mb-3">
                                <material-input id="description" type="text" label="Description" name="description"
                                    v-model="organization.description" />
                            </div>
                        </div>
                    </div>

                    <div class="row">
                        <div class="col-md-6">
                            <div class="mb-3">
                                <material-input id="email" type="email" label="Email" name="email"
                                    v-model="organization.email" :error="!!organizationEmailError" @blur="validateOrganizationEmail" />
                                <small v-if="organizationEmailError" class="text-danger">{{ organizationEmailError }}</small>
                            </div>
                        </div>
                        <div class="col-md-6">
                            <div class="mb-3">
                                <material-input id="phone" type="tel" label="Phone Number" name="phone"
                                    v-model="organization.phone" />
                            </div>
                        </div>
                    </div>

                    <div class="row">
                        <div class="col-md-6">
                            <div class="mb-3">
                                <MaterialSelect id="status" label="Status" name="status"
                                    v-model="organization.status" :options="statusOptions" />
                            </div>
                        </div>
                        <div class="col-md-6">
                            <div class="mb-3">
                                <material-input id="balance" type="text" label="Current Balance" name="balance"
                                    :modelValue="organizationBalanceDisplay" :read-only="true" />
                            </div>
                        </div>
                    </div>

                    <div class="row">
                        <div class="col-12 d-flex justify-content-end">
                            <MaterialButton type="submit" variant="primary" class="px-4" :disabled="isSaving"
                                :class="{ 'btn-pulse': hasChanges && !isSaving }">
                                <span v-if="isSaving" class="spinner-border spinner-border-sm me-2" role="status"
                                    aria-hidden="true"></span>
                                <i v-else class="material-icons text-lg me-1">save</i>
                                {{ isSaving ? 'Saving...' : 'Save' }}
                            </MaterialButton>
                        </div>
                    </div>
                </form>
            </template>
        </FormContainer>

        <!-- Locations Table -->
        <div class="table-responsive" style="max-height: 600px; overflow-x: auto;">
            <BaseTable :loading="loading" :rows="locations" :columns="columns" searchable :selectable="false"
                heading="Locations">
                <template #buttons>
                    <button class="btn btn-info btn-sm" @click="openAddNewLocationModal()">
                        <i class="material-icons text-lg text-white">add</i> Add New
                    </button>
                </template>
                <template #actions="{ row }">
                    <button class="btn btn-sm my-0 py-0" @click="editLocation(row.locationid)">
                        <i class="material-icons text-lg text-orange">edit</i>
                    </button>
                    <button class="btn btn-sm my-0 py-0" @click="openDeleteModal(row)">
                        <i class="material-icons text-lg text-danger">delete</i>
                    </button>
                </template>
            </BaseTable>
        </div>

        <!-- Delete Modal -->
        <DeleteModal v-model:show="showDeleteModal" :itemName="selectedLocation?.name" @confirm="deleteSkill" />

        <!-- Admin Selector -->
        <FormContainer heading="Select Admins for this organization">
            <template v-slot:content>
                <TagSelector :loading="loading" class="py-2" mainHeading="" optionName="users"
                    availableOptionsHeading="All Users" selectedOptionsHeading="Current Admins"
                    :initial-skills="availableUsers" v-model:interests="userInterests"
                    @save-interests="handleSaveInterests" />
            </template>
        </FormContainer>
    </div>

    <!-- Add New Location Modal -->
    <ContentModal v-model="showAddNewLocationModal" title="Add New Location">
        <template #default>
            <form>
                <div class="row">
                    <div class="col-md-6">
                        <div class="mb-3">
                            <material-input id="locationname" type="text" label="Location Name" name="locationname"
                                v-model="organizationLocation.locationname" />
                        </div>
                    </div>
                    <div class="col-md-6 d-flex align-items-end">
                        <div class="mb-3">
                            <label>i.e. 'Atlanta location'</label>
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col-12">
                        <div class="mb-3">
                            <material-input id="description" type="text" label="Description" name="description"
                                v-model="organizationLocation.description" />
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col-md-6">
                        <div class="mb-3">
                            <material-input id="email" type="email" label="Email" name="email"
                                v-model="organizationLocation.email" :error="!!locationEmailError" @blur="validateLocationEmail" />
                            <small v-if="locationEmailError" class="text-danger">{{ locationEmailError }}</small>
                        </div>
                    </div>
                    <div class="col-md-6">
                        <div class="mb-3">
                            <material-input id="phone" type="tel" label="Phone Number" name="phone"
                                v-model="organizationLocation.phone" />
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col-md-6">
                        <div class="mb-3">
                            <MaterialSelect id="country" label="Country" :options="countriesOptions"
                                v-model="organizationLocation.country" />
                        </div>
                    </div>
                    <div class="col-md-6">
                        <div class="mb-3">
                            <MaterialSelect id="timezone" label="Timezone" :options="timezonesOptions"
                                v-model="organizationLocation.timezone" />
                        </div>
                    </div>
                </div>
            </form>
        </template>
        <template #footer>
            <MaterialButton type="submit" variant="primary" class="px-4 m-0 mx-2" :disabled="isSavingLocation"
                @click="saveOrganizationLocation">
                <span v-if="isSavingLocation" class="spinner-border spinner-border-sm me-2" role="status"
                    aria-hidden="true"></span>
                <i v-else class="material-icons text-lg me-1">save</i>
                {{ isSavingLocation ? 'Saving...' : 'Save' }}
            </MaterialButton>
        </template>
    </ContentModal>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import ContentModal from "@/components/Modal/ContentModal.vue"
import MaterialInput from "@/components/common/MaterialInput.vue";
import MaterialButton from "@/components/common/MaterialButton.vue";
import MaterialSelect from "@/components/common/MaterialSelect.vue";
import BaseTable from "@/components/Table/Table.vue";
import DeleteModal from "@/components/Modal/ConfirmationModal.vue";
import TagSelector from "@/components/common/TagSelector.vue";
import FormContainer from "../../components/PageLayout/FormContainer.vue";
import ExplainPageButton from "@/components/common/ExplainPageButton.vue";
import CardLoader from "../../components/Loader/CardLoader.vue";
import { isValidEmail } from "../../utils/validation";

const route = useRoute();
const store = useStore();
const emit = defineEmits(["save"]);
const router = useRouter();

const orgId = computed(() => route.params.organizationID);
const loading = ref(false);
const isSaving = ref(false);
const isSavingLocation = ref(false);
const showAddNewLocationModal = ref(false);
const organizationEmailError = ref(null);
const locationEmailError = ref(null);

const organization = reactive({
    id: null,
    name: "",
    description: "",
    email: "",
    phone: "",
    status: "inactive",
});

// ── Track unsaved changes for Save button pulse ──
const originalOrganization = ref(null)
const hasChanges = computed(() => {
    if (!originalOrganization.value) return false
    const orig = originalOrganization.value
    return orig.name !== organization.name
        || orig.description !== organization.description
        || orig.email !== organization.email
        || orig.phone !== organization.phone
        || orig.status !== organization.status
})

const organizationLocation = ref({
    locationname: "",
    description: "",
    email: "",
    phone: "",
    country: "",
    timezone: ""
});

const statusOptions = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
];

const locations = ref([]);
const columns = ref([
    { key: "locationid", label: "ID", sortable: true },
    { key: "locationname", label: "Name", sortable: true },
    { key: "current_balance", label: "Balance", sortable: true },
    { key: "status", label: "Status", sortable: true },
    { key: "description", label: "Description", sortable: true },
    { key: "phone", label: "Phone", sortable: true },
    { key: "email", label: "Email", sortable: true },
    { key: "country", label: "Country", sortable: true },
    { key: "time_zone", label: "Timezone", sortable: true },
    { key: "createddtm", label: "Created Date", sortable: true },
    { key: "autoenginestartdt", label: "Engine Start", sortable: true },
    { key: "autoengineenddt", label: "Engine End", sortable: true },
]);

const showDeleteModal = ref(false);
const selectedLocation = ref(null);
const availableUsers = ref([]);
const userInterests = ref([]);
const organizationBalance = ref(null);

const organizationBalanceDisplay = computed(() => {
    const balance = organizationBalance.value?.current_balance;
    if (balance === undefined || balance === null) return 'Loading...';
    return `$${balance}`;
});

const validateOrganizationEmail = () => {
    if (organization.email && organization.email.trim() !== '') {
        if (!isValidEmail(organization.email)) {
            organizationEmailError.value = 'Please enter a valid email address';
        } else {
            organizationEmailError.value = null;
        }
    } else {
        organizationEmailError.value = null;
    }
};

const validateLocationEmail = () => {
    if (organizationLocation.value.email && organizationLocation.value.email.trim() !== '') {
        if (!isValidEmail(organizationLocation.value.email)) {
            locationEmailError.value = 'Please enter a valid email address';
        } else {
            locationEmailError.value = null;
        }
    } else {
        locationEmailError.value = null;
    }
};

async function loadOrganization(id) {
    const data = await store.dispatch("fetchOrganizationById", id);
    if (data) {
        organization.id = data.organizationid;
        organization.name = data.organizationname;
        organization.description = data.description;
        organization.email = data.email;
        organization.phone = data.phone;
        organization.status = data.status;
        locations.value = data.locations || [];

        // Snapshot for change detection
        originalOrganization.value = { ...organization };


        // Fetch balance for each location
        await fetchLocationBalances();
    }
}

async function fetchLocationBalances() {
    const balancePromises = locations.value.map(async (loc) => {
        try {
            const result = await store.dispatch("fetchLocationBalance", loc.locationid);
            loc.current_balance = result?.current_balance != null ? `$${result.current_balance}` : 'N/A';
        } catch {
            loc.current_balance = 'N/A';
        }
    });
    await Promise.allSettled(balancePromises);
}

async function loadAvailableUsersAndAdmins(id) {
    try {
        const response = await store.dispatch("fetchAdminsForOrganization", id);
        const organizationUsers = response.organization_users;
        const currentAdmins = response.current_admins;

        availableUsers.value = organizationUsers.map((a) => ({
            id: a.personid ?? a.person_id ?? a.id,
            name: a.display_name,
            description: a.email ?? "",
        }));

        userInterests.value = currentAdmins.map((a) => ({
            id: a.personid ?? a.person_id ?? a.id,
            name: a.display_name,
            description: a.email ?? "",
        }));
    } catch (err) {
        console.error("Error fetching admins:", err);
    }
}

onMounted(async () => {
    const id = orgId.value;
    if (!id) return;
    loading.value = true;
    try {
        await Promise.allSettled([
            loadOrganization(id),
            loadAvailableUsersAndAdmins(id),
            store.dispatch("getCountries"),
            store.dispatch("getTimezones"),
            store.dispatch("fetchOrganizationBalance", id).then((result) => {
                organizationBalance.value = result;
            }).catch(() => {
                organizationBalance.value = { current_balance: 'N/A' };
            })
        ]);
    } catch (err) {
        console.error("Error loading organization:", err);
        alert("Failed to load organization details.");
    } finally {
        loading.value = false;
    }
});

async function saveOrganization() {
    const id = orgId.value;
    if (!id) {
        alert("Organization ID missing.");
        return;
    }

    // Validate email before saving
    validateOrganizationEmail();
    if (organizationEmailError.value) {
        alert("Please fix validation errors before saving.");
        return;
    }

    isSaving.value = true;
    try {
        await store.dispatch("updateOrganization", {
            id,
            payload: {
                organizationname: organization.name,
                description: organization.description,
                email: organization.email,
                phone: organization.phone,
                status: organization.status,
            },
        });
        emit("save", organization);
        alert("Organization updated successfully!");
    } catch (err) {
        console.error("Update failed:", err);
        alert("Failed to update organization.");
    } finally {
        isSaving.value = false;
    }
}

function openAddNewLocationModal() {
    organizationLocation.value = {
        locationname: "",
        description: "",
        email: "",
        phone: "",
        country: "",
        timezone: ""
    };
    locationEmailError.value = null;
    showAddNewLocationModal.value = true;
}

async function saveOrganizationLocation() {
    // Validate email before saving
    validateLocationEmail();
    if (locationEmailError.value) {
        alert("Please fix validation errors before saving.");
        return;
    }

    isSavingLocation.value = true;
    try {
        const res = await store.dispatch("createLocation", {
            ...organizationLocation.value,
            organizationid: orgId.value
        });

        // Refresh locations list
        await loadOrganization(orgId.value);

        showAddNewLocationModal.value = false;

        // Navigate to edit the newly created location
        router.push({ name: "EditLocation", params: { locationID: res.output.location.locationid } });
    } catch (error) {
        console.error("Error saving organization location:", error);
        alert("Failed to save location.");
    } finally {
        isSavingLocation.value = false;
    }
}

function openDeleteModal(location) {
    selectedLocation.value = location;
    showDeleteModal.value = true;
}

function editLocation(id) {
    router.push({ name: "EditLocation", params: { locationID: id } });
}

function deleteSkill() {
    if (!selectedLocation.value) return;
    locations.value = locations.value.filter((s) => s.id !== selectedLocation.value.id);
    showDeleteModal.value = false;
}

async function handleSaveInterests(selected = null) {
    const id = orgId.value;

    if (!id) {
        alert("Organization ID missing.");
        return;
    }

    try {
        const selectedItems = Array.isArray(selected) && selected.length ? selected : userInterests.value || [];

        const userIds = selectedItems
            .map((item) => {
                if (item == null) return null;
                if (typeof item === "number") return item;
                if (typeof item === "string" && /^\d+$/.test(item)) return Number(item);

                return (
                    item.personid ??
                    item.personId ??
                    item.person_id ??
                    item.userid ??
                    item.user_id ??
                    item.id ??
                    (item.raw && (item.raw.personid ?? item.raw.id)) ??
                    null
                );
            })
            .filter((x) => x !== null && x !== undefined && !Number.isNaN(x));

        const uniqueIds = Array.from(new Set(userIds));
        console.log("uniqueIds", uniqueIds);

        await store.dispatch("assignAdminsToOrganization", { orgId: id, userIds: uniqueIds });
        await store.dispatch("fetchAdminsForOrganization", id);
    } catch (err) {
        console.error("Failed to assign admins:", err);
    }
}

const countriesOptions = computed(() => {
    const countries = store.getters["stCountries"] || {};
    return Object.keys(countries).map(key => ({
        label: countries[key],
        value: key
    }));
});

const timezonesOptions = computed(() => {
    const zones = store.getters["stTimezones"] || {};
    return Object.keys(zones).map(key => ({
        label: zones[key],
        value: key
    }));
});
</script>

<style scoped>
/* ── Save button pulse ── */
@keyframes btn-pulse-glow {
    0%   { box-shadow: 0 0 0 0 rgba(99, 102, 241, 0.55); }
    70%  { box-shadow: 0 0 0 10px rgba(99, 102, 241, 0); }
    100% { box-shadow: 0 0 0 0 rgba(99, 102, 241, 0); }
}
.btn-pulse {
    animation: btn-pulse-glow 1.5s infinite;
}

.card {
    border: none;
    border-radius: 8px;
}

.form-label {
    font-weight: 600;
    color: #495057;
    margin-bottom: 0.5rem;
}

.form-control:focus {
    border-color: #80bdff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

/* Dashboard heading */
.dashboard-heading {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1rem 1.25rem;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}
.dh-icon-wrap {
    width: 52px;
    height: 52px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: #fff;
}
.dh-icon-wrap i { font-size: 28px; }
.dh-icon-org { background: linear-gradient(135deg, #ab47bc, #7b1fa2); }
.dh-title {
    margin: 0;
    font-size: 1.15rem;
    font-weight: 700;
    color: #344767;
}
.dh-subtitle {
    margin: 0;
    font-size: 0.78rem;
    color: #7b809a;
    margin-top: 2px;
}
</style>
